/**
 * Bağlantı değerleri (0.4.1, artboard F_Baglanti): the fields where Sercan enters the
 * Telegram bot token and his Telegram id, the Claude OAuth token, the two Genesis keys, the
 * Etsy app credentials and the shop mailbox (v0.1.5).
 * The panel sends a value once and forgets it: the server seals it and only ever answers
 * with the last four characters ("kaydedildi ...7XVQ"). Kaydet replaces, Sil clears,
 * Bağlantıyı dene asks the server to try the connection (the worker runs Telegram getMe and
 * the Genesis usage call; the Claude token gets a format check). Every control is 44 px.
 */

import {
  CONNECTION_SLOT_PANEL_ID,
  CONNECTION_SLOTS,
  type ConnectionSlot,
  type ConnectionSlotState,
  connectionValueAccepted,
  SECRET_CONNECTION_SLOTS,
} from '@twin/contracts';
import { type FormEvent, useId, useState } from 'react';
import { usePanel, useStore } from '../app/context.js';
import { Icon } from './icons.js';

export interface SlotCopy {
  label: string;
  /** Where the value comes from, one line. */
  hint: string;
  placeholder: string;
  /** The slot is stored but nothing reads it yet. */
  unused?: string;
}

export const SLOT_COPY: Readonly<Record<ConnectionSlot, SlotCopy>> = {
  telegram_bot_token: {
    label: "Bot token'ı",
    hint: "Telegram'da @BotFather > /newbot; verdiği token",
    placeholder: '123456789:AA...',
  },
  telegram_owner_id: {
    label: 'Telegram kullanıcı kimliğin',
    hint: "Telegram'da @userinfobot'a bir mesaj at; Id satırındaki sayı. Başka herkes yok sayılır",
    placeholder: '123456789',
  },
  anthropic_api_key: {
    label: 'Genel Müdür API anahtarı',
    hint: 'console.anthropic.com › API Keys › Create Key; kredinin olduğu kuruluşta aç. Girince Twin dener, Genel Müdür bu anahtara geçer',
    placeholder: 'sk-ant-api03-...',
  },
  claude_oauth_token: {
    label: "Claude token'ı",
    hint: 'Kendi bilgisayarında: claude setup-token; çıkan değer. GM ilk çalıştığında doğrulanır',
    placeholder: 'sk-ant-oat01-...',
  },
  genesis_twin_prod: {
    label: 'twin-prod anahtarı',
    hint: 'Genesis panelinden (genesis.bmuworks.com) Twin için açtığın anahtar; sunucunun IP adresine kilitli',
    placeholder: 'bmu_live_...',
  },
  genesis_ajan_gorevleri: {
    label: 'ajan görevleri anahtarı',
    hint: 'Genesis panelindeki ikinci anahtar; Temsilci (müşteri e-postaları ve görselleri) yalnız bununla çalışır, twin-prod ile değil',
    placeholder: 'bmu_live_...',
  },
  etsy_keystring: {
    label: 'Keystring (API anahtarı)',
    hint: 'Etsy geliştirici paneli (etsy.com/developers/your-apps) > uygulamanın satırındaki Keystring',
    placeholder: '24 küçük harf ve rakam',
    unused: 'mağaza bağlantısı (Bağla) sonraki sürümde',
  },
  etsy_shared_secret: {
    label: 'Shared secret',
    hint: 'Aynı satırda, göz simgesiyle açılır; sohbete veya ekran görüntüsüne koyma',
    placeholder: 'shared secret',
    unused: 'mağaza bağlantısı (Bağla) sonraki sürümde',
  },
  gmail_address: {
    label: 'Gmail adresi',
    hint: 'Müşteri sorularının düştüğü posta kutusu',
    placeholder: 'ornek@gmail.com',
  },
  gmail_app_password: {
    label: 'Uygulama şifresi',
    hint: 'Google hesabı > Güvenlik > 2 Adımlı Doğrulama > Uygulama şifreleri; 16 harf, boşluklu da olur',
    placeholder: 'abcd efgh ijkl mnop',
  },
  brave_search_key: {
    label: 'API anahtarı',
    hint: 'brave.com/search/api › API Keys; "Data for AI" planı, ayda 5 $ kredi (yaklaşık 1.000 sorgu)',
    placeholder: 'BSA…',
  },
  serper_api_key: {
    label: 'API anahtarı',
    hint: 'serper.dev › API key; 2.500 ücretsiz sorgu, sonra 50 $ = 50.000 sorgu',
    placeholder: '40 harf ve rakam',
  },
  google_places_key: {
    label: 'API anahtarı',
    hint: 'console.cloud.google.com › Credentials; Places API (New) etkin ve faturalandırma bağlı olmalı; ayda 5.000 işletme araması ücretsiz',
    placeholder: 'AIza…',
  },
  gemini_api_key: {
    label: 'API anahtarı (eski)',
    hint: 'Bu arama artık Genesis üzerinden (Genesis Eye) çalışıyor; anahtar gerekmez. Kayıtlı değer silinmez ve kullanılmaz; istersen Sil ile kaldır. Bağlantıyı dene, Genesis üzerinden bir Google araması dener',
    placeholder: 'gerekmez',
    unused: 'artık gerekmez',
  },
  here_api_key: {
    label: 'API anahtarı',
    hint: 'platform.here.com › Access manager › API keys (Limited plan kart istemez); günde 1.000 istek ücretsiz',
    placeholder: 'harf, rakam, tire',
  },
  abn_lookup_guid: {
    label: 'GUID',
    hint: 'abr.business.gov.au › Tools › Web services kaydı; e-postayla gelen GUID',
    placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
  },
  nzbn_api_key: {
    label: 'API anahtarı',
    hint: 'api.business.govt.nz › Products › NZBN aboneliği; 32 haneli anahtar',
    placeholder: '32 hane',
  },
  outreach_cgp_address: {
    label: 'Gönderim kutusu adresi',
    hint: 'cadgaragepro.net üzerindeki Google Workspace kutusu (sercan@cadgaragepro.net)',
    placeholder: 'sercan@cadgaragepro.net',
  },
  outreach_cgp_app_password: {
    label: 'Gönderim kutusunun uygulama şifresi',
    hint: 'Bu kutunun Google hesabında 2 Adımlı Doğrulama açıkken Uygulama şifreleri sayfasından alınan 16 harf',
    placeholder: 'abcd efgh ijkl mnop',
  },
  // v0.1.170: the Dogo mailbox is retired; its values are not read, only removed with Sil.
  outreach_dogo_address: {
    label: 'Dogo gönderim kutusu adresi (kullanılmıyor)',
    hint: 'Dogo adına mail gönderilmiyor (11 Ekim kararı); bu değeri girme. Kayıtlıysa Sil ile kaldır.',
    placeholder: 'kullanılmıyor',
  },
  outreach_dogo_password: {
    label: 'Dogo kutusunun şifresi (kullanılmıyor)',
    hint: 'Dogo adına mail gönderilmiyor (11 Ekim kararı); bu değeri girme. Kayıtlıysa Sil ile kaldır.',
    placeholder: 'kullanılmıyor',
  },
  ga4_service_account: {
    label: 'Hizmet hesabı anahtarı (JSON)',
    hint: "Google Cloud › IAM › Hizmet hesapları › Anahtarlar › JSON; indirilen dosyanın tamamını yapıştır. Hesaba GA4'te Görüntüleyici ver",
    placeholder: '{"type": "service_account", ...}',
  },
  bing_webmaster_key: {
    label: 'Bing Webmaster API anahtarı',
    hint: 'bing.com/webmasters › Ayarlar (sağ üst) › API Erişimi › koşulları kabul et › API Anahtarı › Oluştur; kullanıcı başına bir anahtar',
    placeholder: '32 harf ve rakam',
  },
  youtube_api_key: {
    label: 'YouTube Data API anahtarı',
    hint: 'console.cloud.google.com › API’ler ve Hizmetler › Kimlik bilgileri › API anahtarı; projede YouTube Data API v3 etkin olmalı',
    placeholder: 'AIza...',
  },
  pinterest_app_secret: {
    label: 'Pinterest uygulama gizli anahtarı',
    hint: 'developers.pinterest.com › Apps › CadGaragePro Twin › gizli anahtar (Göster düğmesi); kaydettikten sonra Bağla',
    placeholder: 'Pinterest uygulamasının gizli anahtarı',
  },
  pinterest_sandbox_token: {
    label: 'Deneme ortamı anahtarı (sandbox token)',
    hint: 'developers.pinterest.com › Apps › CadGaragePro Twin › sandbox token oluştur (Generate); yalnız uygulama Deneme (Trial) erişimindeyken pinler bununla yayınlanır, Standard erişimde kullanılmaz',
    placeholder: 'pina_...',
  },
  cgtrader_cookie: {
    label: 'CGTrader oturum çerezi',
    hint: 'cgtrader.com’a girişliyken F12 › Network › sayfayı yenile › ilk istek › Request Headers › cookie değeri; tek satır. Birkaç haftada bir yenilenir',
    placeholder: '_cgtrader_session=...; ...',
  },
  ga4_property_id: {
    label: 'GA4 mülk numarası',
    hint: 'GA4 › Yönetici › Mülk ayarları; yalnız rakamlar (G- ile başlayan ölçüm kimliği değil)',
    placeholder: '123456789',
  },
  reoon_api_key: {
    label: 'Reoon API anahtarı',
    hint: 'emailverifier.reoon.com › API; günde 20 kontrol ücretsiz, mailden önce adresin gerçekten mail aldığını kontrol eder',
    placeholder: 'Reoon panelindeki API anahtarı',
  },
  millionverifier_api_key: {
    label: 'MillionVerifier API anahtarı (isteğe bağlı)',
    hint: 'app.millionverifier.com › API; mailden önce adresin gerçekten mail aldığını kontrol eder',
    placeholder: 'harf ve rakam',
  },
  portal_mcp_url: {
    label: 'Portal teslimat adresi',
    hint: 'Portal oturumunun Twin için açtığı bmu-mcp adresi (yalnız teslimat araçları); kendi kişisel adresini girme',
    placeholder: 'https://app.bmuworks.com/mcp/…',
  },
};

/** The panel connections whose grant is entered here. */
export function slotsOfConnection(connectionId: string): ConnectionSlot[] {
  return CONNECTION_SLOTS.filter((slot) => CONNECTION_SLOT_PANEL_ID[slot] === connectionId);
}

export function isSlotConnection(connectionId: string): boolean {
  return slotsOfConnection(connectionId).length > 0;
}

export type SlotStatusTone = 'empty' | 'set' | 'testing' | 'error' | 'ok';

/** The status line of a slot: bağlı değil, kaydedildi ...7XVQ, deneniyor, hata, bağlı @bot_adı. */
export function slotStatus(state: ConnectionSlotState | undefined): { tone: SlotStatusTone; text: string } {
  if (state === undefined || state.status === 'empty') return { tone: 'empty', text: 'bağlı değil' };
  const saved = `kaydedildi ...${state.last4 ?? ''}`;
  const test = state.test;
  if (test === null) return { tone: 'set', text: saved };
  if (test.status === 'queued' || test.status === 'running') return { tone: 'testing', text: 'deneniyor' };
  if (test.status === 'failed') return { tone: 'error', text: `hata${test.detail ? ` · ${test.detail}` : ''}` };
  return { tone: 'ok', text: `bağlı${test.detail ? ` ${test.detail}` : ''}` };
}

function SlotRow({ slot, state }: { slot: ConnectionSlot; state: ConnectionSlotState | undefined }) {
  const store = useStore();
  const { connectionBusy } = usePanel();
  const copy = SLOT_COPY[slot];
  const secret = SECRET_CONNECTION_SLOTS.includes(slot);
  const [value, setValue] = useState('');
  const [shown, setShown] = useState(false);
  const inputId = useId();
  const statusId = useId();
  const hintId = useId();
  const busy = connectionBusy.includes(slot);
  const status = slotStatus(state);
  const isSet = state?.status === 'set';
  const trimmed = value.trim();
  const acceptable = trimmed !== '' && connectionValueAccepted(slot, trimmed);
  const testable = state?.testable === true && isSet;

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || !acceptable) return;
    const ok = await store.saveConnection(slot, trimmed);
    if (ok) {
      setValue('');
      setShown(false);
    }
  };

  return (
    <li className="conn-slot">
      <form className="conn-slot__form" onSubmit={save} noValidate aria-busy={busy}>
        <div className="conn-slot__head">
          <label className="conn-slot__label" htmlFor={inputId}>
            {copy.label}
            {copy.unused ? <span className="conn-slot__unused"> · {copy.unused}</span> : null}
          </label>
          <span id={statusId} className={`conn-slot__status conn-slot__status--${status.tone}`} aria-live="polite">
            {status.tone === 'ok' ? <Icon name="check" size={12} stroke={2.5} /> : null}
            {status.tone === 'testing' ? <Icon name="timer" size={12} /> : null}
            {status.tone === 'error' ? <Icon name="info" size={12} /> : null}
            {status.text}
          </span>
        </div>
        <p id={hintId} className="conn-slot__hint">
          {copy.hint}
        </p>
        <div className="conn-slot__field">
          <input
            id={inputId}
            className="field__input conn-slot__input"
            type={secret && !shown ? 'password' : 'text'}
            inputMode={slot === 'telegram_owner_id' ? 'numeric' : 'text'}
            autoComplete="off"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder={isSet ? 'yeni değer (eskisinin yerine geçer)' : copy.placeholder}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            aria-describedby={`${hintId} ${statusId}`}
            aria-invalid={trimmed !== '' && !acceptable}
            disabled={busy}
          />
          {secret ? (
            <button
              type="button"
              className="conn-slot__reveal"
              aria-label={shown ? `${copy.label}: gizle` : `${copy.label}: göster`}
              aria-pressed={shown}
              onClick={() => setShown((current) => !current)}
            >
              <Icon name={shown ? 'eye-off' : 'eye'} size={18} />
            </button>
          ) : null}
        </div>
        {trimmed !== '' && !acceptable ? (
          <p className="conn-slot__invalid" role="status">
            Değer beklenen biçimde değil; kopyaladığın değeri kontrol et.
          </p>
        ) : null}
        <div className="conn-slot__acts">
          <button
            type="submit"
            className="btn btn--primary conn-slot__btn"
            disabled={busy || !acceptable}
            aria-label={`${copy.label}: kaydet${isSet ? ', eskisinin yerine geçer' : ''}`}
          >
            Kaydet
          </button>
          {testable ? (
            <button
              type="button"
              className="btn btn--secondary conn-slot__btn"
              disabled={busy}
              aria-label={`${copy.label}: bağlantıyı dene, sunucu tarafında`}
              onClick={() => void store.testConnection(slot)}
            >
              <Icon name="refresh-cw" size={14} />
              {status.tone === 'testing' ? 'Deneniyor…' : 'Bağlantıyı dene'}
            </button>
          ) : null}
          {isSet ? (
            <button
              type="button"
              className="text-btn conn-slot__delete"
              disabled={busy}
              aria-label={`${copy.label}: kayıtlı değeri sil`}
              onClick={() => void store.clearConnection(slot)}
            >
              Sil
            </button>
          ) : null}
        </div>
      </form>
    </li>
  );
}

/** The value fields of a connection (Telegram, Claude, Genesis) in the detail screen. */
export function ConnectionSecrets({ connectionId, pane = false }: { connectionId: string; pane?: boolean }) {
  const { connections, connectionsError } = usePanel();
  const store = useStore();
  const Sub = pane ? 'h3' : 'h2';
  const slots = slotsOfConnection(connectionId);
  const id = `conn-${connectionId}-values`;
  if (slots.length === 0) return null;
  return (
    <section aria-labelledby={id} className="conn-sec conn-values">
      <Sub id={id} className="key ink-2 conn-sec__title">
        Bağlantı değerleri
      </Sub>
      {connections === null && connectionsError !== null ? (
        <p className="conn-values__error" role="status">
          Değerler okunamadı: {connectionsError}{' '}
          <button type="button" className="text-btn" onClick={() => void store.loadConnections()}>
            Tekrar dene
          </button>
        </p>
      ) : null}
      <ul className="conn-slots">
        {slots.map((slot) => (
          <SlotRow key={slot} slot={slot} state={connections?.slots.find((item) => item.slot === slot)} />
        ))}
      </ul>
      <p className="conn-values__note">
        <Icon name="lock" size={12} stroke={2} />
        Değerler sadece sunucuda, şifreli tutulur; kaydettikten sonra bir daha gösterilmez. Yeni bir değer eskisinin
        yerine geçer.
      </p>
    </section>
  );
}
